import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { createEmgAuth, type EmgAuthClaims } from './authClient';
import { AuthContext, type AuthContextValue, type AuthStatus } from './AuthContext';
import { clearSignInAttempts, isSignInLooping, requestSignIn } from './signInLoop';
import { SessionErrorCard } from '@/components/SessionErrorCard';

/**
 * EAA-1756: auth guard. On mount (and on every route change) it checks for a
 * valid session via the auth-client; if there is none and the user is not on
 * `/callback` (which owns the code exchange itself), it redirects to Keycloak
 * via `login()`. Also listens for `onSessionEnded` (expiry / RP-initiated
 * sign-out) and re-triggers login so an ended session never leaves the app in
 * a stale "authenticated" state.
 *
 * EAA-1825: every one of those redirects now goes through `requestSignIn`, and
 * the provider owns `reauthenticate()` — the single entry point screens use when
 * an API call comes back 401. The old `loginTriggered` ref was replaced because
 * it only de-duplicated within ONE page load, and each leg of the loop it was
 * meant to stop IS a fresh page load, so it never saw the repetition. When the
 * guard refuses to redirect again this provider renders `SessionErrorCard` in
 * place of the app: the session cannot be fixed by signing in, so showing the
 * shell (whose every request 401s) would be a lie.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
    const config = useRuntimeConfig();
    const location = useLocation();

    const client = useMemo(
        () =>
            createEmgAuth({
                authority: config.authority,
                clientId: config.clientId,
                redirectUri: `${window.location.origin}/callback`,
            }),
        [config.authority, config.clientId]
    );

    const [state, setState] = useState<{
        status: AuthStatus;
        claims: EmgAuthClaims | null;
        accessToken: string | null;
    }>({ status: 'loading', claims: null, accessToken: null });

    const currentPath = `${location.pathname}${location.search}`;

    // Kept in a ref-free closure over the latest path: a redirect must return to
    // where the user actually is, not where they were when the provider mounted.
    const reauthenticate = useCallback(
        (returnTo?: string) => {
            void (async () => {
                const result = await requestSignIn(
                    (target) => client.login(target),
                    returnTo ?? currentPath
                );
                if (result === 'looping') {
                    setState((prev) => ({ ...prev, status: 'session-error' }));
                }
            })();
        },
        [client, currentPath]
    );

    useEffect(() => {
        // /callback exchanges the authorization code itself (CallbackPage) and
        // /login (EAA-1765) owns the returnTo-aware sign-in — the guard must not
        // race either with its own login() redirect (it would clobber the
        // external returnTo with the portal-relative path).
        if (location.pathname === '/callback' || location.pathname === '/login') {
            return;
        }

        let cancelled = false;

        (async () => {
            const accessToken = await client.getAccessToken();
            if (cancelled) return;

            if (!accessToken) {
                // EAA-1825: a token-less load that keeps coming back token-less is
                // the same loop seen from the other side — spend the guarded budget
                // rather than redirecting unconditionally.
                if (isSignInLooping()) {
                    setState({ status: 'session-error', claims: null, accessToken: null });
                    return;
                }
                setState({ status: 'unauthenticated', claims: null, accessToken: null });
                reauthenticate();
                return;
            }

            const claims = await client.getClaims();
            if (cancelled) return;
            setState({ status: 'authenticated', claims, accessToken });
        })();

        return () => {
            cancelled = true;
        };
    }, [client, location.pathname, location.search, reauthenticate]);

    useEffect(
        () =>
            client.onSessionEnded(() => {
                setState({ status: 'unauthenticated', claims: null, accessToken: null });
                reauthenticate();
            }),
        [client, reauthenticate]
    );

    const handleRetry = useCallback(() => {
        // The user chose to try again, so the loop is over by decision: restore a
        // full budget and take one more run at the IdP.
        clearSignInAttempts();
        setState((prev) => ({ ...prev, status: 'loading' }));
        reauthenticate();
    }, [reauthenticate]);

    const logout = useCallback(async () => {
        clearSignInAttempts();
        await client.logout();
    }, [client]);

    const value = useMemo<AuthContextValue>(
        () => ({
            client,
            status: state.status,
            authenticated: state.status === 'authenticated',
            claims: state.claims,
            accessToken: state.accessToken,
            reauthenticate,
            logout,
        }),
        [client, state, reauthenticate, logout]
    );

    let content = children;
    if (state.status === 'session-error') {
        content = <SessionErrorCard onRetry={handleRetry} onSignOut={logout} />;
    }

    return <AuthContext.Provider value={value}>{content}</AuthContext.Provider>;
}
